import { DateField } from "components/DesignSystem/DateInput/DateField";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { EditPencil } from "components/icons/EditPencil";
import { TriangleInfo } from "components/icons/TriangleInfo";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { Form, FormikValues } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import {
  useUpdateTaskAssigneeAdminMutation,
  useUpdateTaskAssigneeProviderMutation,
} from "store/apis/serviceTeam";
import { useUpdateBulkTaskMutation } from "store/apis/task";
import { Task } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { object, string } from "yup";

type TaskBulkDeadlineUpdateProps = {
  isOpen: boolean;
  close: () => void;
  selectedRows: string[];
  resetSelection: () => void;
};

export const UpdateBulkTaskDeadline = ({
  isOpen,
  close,
  selectedRows,
  resetSelection,
}: TaskBulkDeadlineUpdateProps) => {
  const { alertToast, successToast } = useToast();
  const [bulkTaskUpdate, { isLoading }] = useUpdateBulkTaskMutation();

  const handleSubmit = async (values: FormikValues) => {
    try {
      if (!values.deadline) {
        alertToast({ message: "Please select a valid date" });
        return;
      }
      await bulkTaskUpdate({
        payload: {
          task_ids: selectedRows,
          deadline_date: dayjs(values.deadline).format(YYYY_MM_DD),
        },
      }).unwrap();
      close();
      successToast({
        title: "Deadline updated",
        message: `Deadline updated for ${selectedRows.length} tasks`,
      });
      resetSelection();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <Modal.Root onOpenChange={close} open={isOpen}>
      <Modal.Content asChild>
        <FormikForm
          initialValues={{ deadline: "" }}
          validationSchema={() =>
            object({
              deadline: string().required("Deadline is required"),
            })
          }
          onSubmit={handleSubmit}
        >
          {({ setFieldValue, submitForm }) => (
            <>
              <Modal.Header>
                <Modal.Title>Edit Task Deadline</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <DateField
                  name="deadline"
                  dateProps={{
                    label: "Deadline",
                    required: true,
                    onDateChange: (date: Date) =>
                      setFieldValue("deadline", dayjs(date).format(YYYY_MM_DD)),
                    rightComponent: (
                      <CalendarIcon className="t-text-text-30 -t-mt-0.5" />
                    ),
                  }}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  type="submit"
                  customType="primary"
                  isLoading={isLoading}
                  disabled={isLoading}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
